import React, { useState, useEffect } from 'react';
import { X, Filter, ChevronDown, ChevronRight, ChevronLeft, Menu } from 'lucide-react';
import { getSession } from '../../../../getSession';
import API_ENDPOINTS from '../../../../config/apiEndpoints';

const EnquiryFilterSidebar = ({ isOpen, onOpen, onClose, onApply, mainFields }) => {
  const [customFields, setCustomFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState({});
  const [fieldValues, setFieldValues] = useState({});
  const [fieldOperators, setFieldOperators] = useState({});
  const [loading, setLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const { TokenId, parentId } = getSession();
  const NEWV3_BASE_URL = (process.env.REACT_APP_SERVICES_API_BASE_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchCustomFields();
  }, []);

  const fetchCustomFields = async () => {
    setLoading(true);
    try {
      const resolvedParentId = parentId || localStorage.getItem('ParentID') || localStorage.getItem('userId') || '0';
      const payload = {
        Token: TokenId || '-2295521862261168',
        Details: {
          ParentId: resolvedParentId,
          Status: "Active"
        }
      };

      const response = await fetch(`${NEWV3_BASE_URL}${API_ENDPOINTS.ENQUIRIES.GET_CUSTOM_FIELDS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.Status === 1 && result.Details) {
          const formattedFields = result.Details.map(f => ({
            label: f.fieldname || f.FieldName,
            value: `cf_${f.field_ID || f.FieldID}`,
            type: f.datatype || f.DataType,
            isCustom: true
          }));
          setCustomFields(formattedFields);
        }
      }
    } catch (error) {
      console.error("Error fetching custom fields:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldToggle = (fieldValue) => {
    setSelectedFields(prev => ({
      ...prev,
      [fieldValue]: !prev[fieldValue]
    }));
    
    // Initialize default operator if not set
    if (!fieldOperators[fieldValue]) {
      setFieldOperators(prev => ({
        ...prev,
        [fieldValue]: 'contains'
      }));
    }
  };

  const handleValueChange = (fieldValue, value, isDateTo = false) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldValue]: isDateTo 
        ? { ...(prev[fieldValue] || {}), to: value }
        : { ...(prev[fieldValue] || {}), from: value, val: value }
    }));
  };

  const handleOperatorChange = (fieldValue, operator) => {
    setFieldOperators(prev => ({
      ...prev,
      [fieldValue]: operator
    }));
  };

  const handleApply = () => {
    const filters = [];
    const allFields = [...mainFields, ...customFields];

    Object.keys(selectedFields).forEach(fieldVal => {
      if (selectedFields[fieldVal]) {
        const fieldDef = allFields.find(f => f.value === fieldVal);
        const valObj = fieldValues[fieldVal] || {};
        const operator = fieldOperators[fieldVal] || 'contains';

        if (fieldDef) {
          if (fieldDef.type === 'date' || fieldDef.type === 'd') {
            if (valObj.from || valObj.to) {
              filters.push({
                field: fieldDef.isCustom ? fieldDef.label : fieldDef.value,
                operator: 'between',
                value: valObj.from || '',
                valueTo: valObj.to || '',
                isCustom: fieldDef.isCustom
              });
            }
          } else {
            if (valObj.val) {
              filters.push({
                field: fieldDef.isCustom ? fieldDef.label : fieldDef.value,
                operator: operator,
                value: valObj.val,
                isCustom: fieldDef.isCustom
              });
            }
          }
        }
      }
    });

    onApply({
      logic: 'ALL',
      rows: filters
    });
  };

  const handleClear = () => {
    setSelectedFields({});
    setFieldValues({});
    setFieldOperators({});
    onApply(null);
  };

  const renderField = (field) => {
    const isSelected = selectedFields[field.value];
    const isDate = field.type === 'date' || field.type === 'd';

    return (
      <div key={field.value} className="mb-2">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 font-medium hover:text-blue-600 transition-colors">
          <input
            type="checkbox"
            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
            checked={!!isSelected}
            onChange={() => handleFieldToggle(field.value)}
          />
          {field.label}
        </label>
        
        {isSelected && (
          <div className="mt-2 ml-6 p-3 bg-slate-50 border border-slate-200 rounded-lg animate-in fade-in slide-in-from-top-2">
            {!isDate && (
              <select
                className="w-full mb-2 p-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={fieldOperators[field.value] || 'contains'}
                onChange={(e) => handleOperatorChange(field.value, e.target.value)}
              >
                <option value="contains">Contains</option>
                <option value="not_contains">Not Contains</option>
                <option value="starts_with">Starts With</option>
                <option value="ends_with">Ends With</option>
                <option value="equals">Equals</option>
              </select>
            )}

            {isDate ? (
              <div className="flex flex-col gap-2">
                <input
                  type="date"
                  className="w-full p-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="From"
                  value={fieldValues[field.value]?.from || ''}
                  onChange={(e) => handleValueChange(field.value, e.target.value, false)}
                />
                <input
                  type="date"
                  className="w-full p-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="To"
                  value={fieldValues[field.value]?.to || ''}
                  onChange={(e) => handleValueChange(field.value, e.target.value, true)}
                />
              </div>
            ) : (
              <input
                type="text"
                className="w-full p-1.5 text-xs border border-slate-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Search Keywords"
                value={fieldValues[field.value]?.val || ''}
                onChange={(e) => handleValueChange(field.value, e.target.value)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Floating Filter Button */}
      {!isOpen && (
        <div 
          className="absolute left-0 top-1/2 -translate-y-1/2 z-[100] flex items-center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={() => onOpen()}
            className="bg-white border-y border-r border-slate-200 shadow-[2px_0_10px_rgba(0,0,0,0.1)] p-2.5 rounded-r-xl transition-all duration-300 hover:bg-slate-50 group flex items-center"
            style={{ width: isHovered ? 'auto' : '44px' }}
          >
            <Filter size={20} className="text-green-600 shrink-0" strokeWidth={2.5} />
            <span 
              className={`overflow-hidden whitespace-nowrap text-sm font-bold text-slate-700 transition-all duration-300 ${isHovered ? 'w-20 ml-2 opacity-100' : 'w-0 ml-0 opacity-0'}`}
            >
              Filters
            </span>
          </button>
        </div>
      )}

      {/* Sidebar Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-[150] transition-opacity duration-300" onClick={() => onClose(false)} />
      )}

      {/* Sidebar Panel */}
      <div 
        className={`absolute top-0 left-0 h-full w-[320px] bg-white shadow-2xl z-[200] transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Collapse Tab attached to the right edge */}
        {isOpen && (
          <button
            onClick={() => onClose(false)}
            className="absolute -right-8 top-1/2 -translate-y-1/2 w-8 h-12 bg-green-800 text-white flex items-center justify-center rounded-r-md shadow-md hover:bg-green-700 transition-colors"
            style={{ zIndex: -1 }} // So its shadow doesn't overlay the panel, or just z-index: 10
          >
            <ChevronLeft size={20} strokeWidth={3} />
          </button>
        )}

        <div className="flex items-center p-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-blue-600" />
            <h2 className="text-lg font-bold text-slate-800">Filters</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 scrollbar-thin">
          <div className="mb-6">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">Main Fields</h3>
            {mainFields.map(renderField)}
          </div>

          <div className="mb-6">
            <h3 className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-3 px-1">Custom Fields</h3>
            {loading ? (
              <div className="text-xs text-slate-500 text-center py-4">Loading custom fields...</div>
            ) : customFields.length > 0 ? (
              customFields.map(renderField)
            ) : (
              <div className="text-xs text-slate-500 px-1">No custom fields found.</div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50 flex gap-3">
          <button
            onClick={handleApply}
            className="flex-1 bg-[#8bc34a] hover:bg-[#7cb342] text-white py-2 px-4 rounded-lg font-bold text-sm transition-colors"
          >
            Apply
          </button>
          <button
            onClick={handleClear}
            className="flex-1 bg-slate-400 hover:bg-slate-500 text-white py-2 px-4 rounded-lg font-bold text-sm transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
    </>
  );
};

export default EnquiryFilterSidebar;
